/* What a page came to, in one word every screen agrees on.
 *
 * The four outcomes ship at equal visual weight and no screen may invent a
 * fifth phrasing of them, so the rule lives here once and each screen asks.
 *
 *   Marked and explained          1 credit
 *   Marked, one cause not found   1 credit
 *   Topic found, not marked       free
 *   Nothing marked, photo too soft  free
 *
 * **How it is decided: the service's tier letter, mapped.** The service already
 * settles this. `work.tier` scores every read into one of five letters and
 * `api.py` bills on two of them, so deriving the outcome again from findings
 * and mechanisms would put a second copy of the service's rule in TypeScript.
 * That copy has been written and deleted once already in this product, and the
 * note that deleted it named the honest fix: read what the service settled.
 *
 * The service's own definitions, from `edupipe/work.py`:
 *
 *   A  a mechanism was confirmed                    -> marked and explained
 *   B  answers judged, no mechanism named           -> marked, one cause not found
 *   C  topic known, answers not markable            -> topic found, not marked
 *   D  no topic recovered                           -> see below
 *   E  quality failed, topic may still be usable    -> nothing marked, photo too soft
 *
 * The letter never reaches a parent. "Tier A" beside "17 of 20" reads as a
 * grade of the child's paper, and grading the child is the one thing this
 * product must never do. This file is the only one that reads it.
 *
 * **Three things the four outcomes do not cover, and each gets its own value
 * rather than being folded into a neighbour.**
 *
 * `no-topic` is tier D. The contract's list of four has no room for it, and
 * both places it could be folded say something false: "topic found" when none
 * was, or "photo too soft" about a photograph that may be perfectly sharp.
 * Naming a photograph as the problem when it was not is the exact defect this
 * codebase has recorded twice.
 *
 * `not-read` is a page with no report at all: refused by the meter, refused by
 * a full queue, lost to a restart. Nothing was read, so there is no outcome of
 * a read. `say.sayRefusal` writes the sentence for it, because which of those
 * it was changes what a parent should do next.
 *
 * `reading` is a page still in flight. A screen has to render something while
 * it waits, and a null would only move the branch.
 */
import type { PageState, Report } from "./types";

export type Outcome =
  | "explained"
  | "no-cause"
  | "model-marked"
  | "corpus-marked"
  | "topic-only"
  | "no-topic"
  | "too-soft"
  | "not-read"
  | "reading";

/** A settled page carrying a report. `PageDone` satisfies it, and so does the
 *  page the poll recovers from the stored-report route after a restart, which
 *  has no entitlement on it. Structural rather than an import, so this file
 *  stays free of `api.ts` and compiles under Node for the unit tests. */
export interface ReportedPage {
  state: "done";
  billable?: boolean | null;
  /** Whether the request that produced this report settled a debit. The poll
   *  body carries it and, since 5 September 2026, so does the stored-report
   *  route, so a page opened from a list can say what it cost. */
  charged?: boolean | null;
  report: Report;
}

export type Judgeable = PageState | ReportedPage;

/** The contract's own names for the four, for a card that shows one. The three
 *  additions carry names in the same voice. */
export const OUTCOME_LABEL: Record<Outcome, string> = {
  explained: "Marked and explained",
  "no-cause": "Marked, one cause not found",
  "model-marked": "Marked by our reader, not yet verified",
  "corpus-marked": "Marked from our corpus",
  "topic-only": "Topic found, not marked",
  "no-topic": "No topic found",
  "too-soft": "Nothing marked, photo too soft",
  "not-read": "Not read",
  reading: "Reading",
};

/** One sentence a card shows. Second person, about the page and never about
 *  the child, and it states what is free where that is the fact a parent most
 *  wants. A page that was not read has no sentence here worth writing: which
 *  refusal it was decides what to say, and `say.sayRefusal` holds those. */
export const OUTCOME_HEADLINE: Record<Outcome, string> = {
  explained: "We marked this page and named what caused each mistake.",
  "no-cause": "We marked this page, and there is one answer we could not explain.",
  "model-marked": "We have not written a marking library for this kind of "
    + "question yet, so our reader marked it and nobody has checked that "
    + "marking. This page was free.",
  "corpus-marked": "We marked this page using answers held in our corpus. "
    + "Each question says whether its answer was checked by a tutor or is a "
    + "provisional model judgement.",
  "topic-only": "We found the topic on this page and did not mark it. "
    + "This page is free.",
  "no-topic": "We could not work out the topic of this page. This page is free.",
  "too-soft": "The writing is too faint to read in this photograph. "
    + "This page is free.",
  "not-read": "This page was not read.",
  reading: "We are reading this page.",
};

/** The outcomes that wear the card. `not-read` and `reading` are states and
 *  get the error and loading treatments instead. Here rather than beside the
 *  component, so `lib` never has to import a `.tsx` for a type.
 *
 *  Seven of them since wave 2b, and `OUTCOMES` in the card is still five: the
 *  marketing pages promise the five a page can settle on for good, and both
 *  `model-marked` and `corpus-marked` are labelled states a question sits in
 *  until its library is written, after which the same page is one of the five.
 *  Promising either alongside them would be promising the scaffolding. */
export type CardOutcome = Exclude<Outcome, "not-read" | "reading">;

/* --------------------------------------------------------- the corpus, W2b-C
 *
 * A corpus mark is a mark the service has seen agreed twice, by two different
 * learners, or one a tutor has confirmed through the queue. It is not a
 * library's mark and it is not the reader marking on the spot, and a parent is
 * owed the difference.
 *
 * **It is a member of `Outcome`, folded in by the W2b integrator on 5
 * September 2026.** W2b-C built it as a seventh value alongside the union and
 * a card of its own, because widening the union reaches `lib/history.ts` and
 * `ui/OutcomeCard.tsx` and neither is that agent's file. The patch was in the
 * report and this is it applied: one member, two map entries, one card entry,
 * and the result screen back on `outcomeOf` like every other screen. A thread
 * row carries no findings, so `entryOutcome` cannot reach the new value even
 * though its type now admits it.
 */

/** The card's title over a corpus page, and the one sentence under it. Both
 *  read off the two maps above rather than written a second time here: a
 *  sentence a parent reads that exists twice in one file is a sentence that
 *  will one day disagree with itself. */
export const CORPUS_LABEL = OUTCOME_LABEL["corpus-marked"];
export const CORPUS_HEADLINE = OUTCOME_HEADLINE["corpus-marked"];

/** Whether every marked question on this page was marked by the reader rather
 *  than by a library.
 *
 *  `verified === false` is the test, and not `source === "model"`. The service
 *  settles both, and a boolean it sets on exactly one kind of finding is a
 *  smaller thing to depend on than a string this file happens to recognise: a
 *  third source added later would read as "not model" and stay verified, which
 *  is the safe direction. A finding from before either field existed is a
 *  library finding, because the model path did not exist to make it.
 *
 *  **A mixed page is not this outcome.** A page with one library finding and
 *  nine model ones was marked by a library, was charged for by the service on
 *  that library finding, and a card reading "not yet verified" over it would
 *  tell a parent nothing on the page had been checked. So every marked
 *  question has to be a model one, which is also the rule the service bills
 *  on. Nothing here computes a price: `creditCost` still reads the wire. */
function allModelMarked(report: Report): boolean {
  const findings = (report.topics ?? []).flatMap((t) => t.findings ?? []);
  if (findings.length === 0) return false;
  return findings.every((f) => f.verified === false);
}

/** Whether every marked question on this page came from the corpus.
 *
 *  Keyed on `source` and not on `verified`, which is the opposite of the rule
 *  above, and the difference is the point. `verified === false` is the service
 *  saying a mark has nothing behind it; a corpus mark has something behind it,
 *  and `verified` says which, so the field that separates a corpus mark from
 *  every other kind is the one that names where it came from.
 *
 *  A mixed page is not this outcome, for the same reason a mixed page is not
 *  the model one: a page with a library finding on it was marked by a library,
 *  and a card saying otherwise would be describing a page this is not. */
function allCorpusMarked(report: Report): boolean {
  const findings = (report.topics ?? []).flatMap((t) => t.findings ?? []);
  if (findings.length === 0) return false;
  return findings.every((f) => f.source === "corpus");
}

/** Whether nothing on this page was marked by a library: every marked question
 *  came from the reader or from the corpus.
 *
 *  A page that mixes the two is the reader's card and not the corpus's. The
 *  corpus card says we have marked this question before and agreed with
 *  ourselves; a page carrying one unchecked reader mark has a row on it about
 *  which nobody has said that, and the weaker of the two claims is the honest
 *  one to print over the pair. */
function noLibraryMarks(report: Report): boolean {
  if (allModelMarked(report)) return true;
  const findings = (report.topics ?? []).flatMap((t) => t.findings ?? []);
  if (findings.length === 0) return false;
  return findings.every((f) => f.verified === false || f.source === "corpus");
}

/** The four letters a page can carry and still have been marked by nothing a
 *  library wrote. E is the photograph, and a photograph nobody could read has
 *  no marks on it of any kind. */
function unlibraryTier(held: Outcome | undefined): boolean {
  return held === "explained" || held === "no-cause" || held === "topic-only"
    || held === "no-topic";
}

const BY_TIER: Record<string, Outcome> = {
  A: "explained",
  B: "no-cause",
  C: "topic-only",
  D: "no-topic",
  E: "too-soft",
};

/** The service settled it. Read the letter, map it, and stop.
 *
 *  The fallback below runs only where the service sent no letter this file
 *  knows, which no recorded report does. It mirrors `work.tier` in the same
 *  order the service applies it, so a report from a service too old to carry a
 *  tier still lands somewhere honest instead of on "not read". */
function fromReport(report: Report): Outcome {
  const held = BY_TIER[String(report.tier ?? "").trim().toUpperCase()];
  /* The one place the letter is not the whole answer.
   *
   * What separates a library's marking from the reader's is a claim about how
   * far we will stand behind it, and the service states that per finding
   * rather than in the letter. So the letter is read first and then narrowed,
   * which is not a second copy of the rule: it is two fields the service set,
   * read together.
   *
   * **The narrowing covers C and D as well as A and B, corrected by the wave 2
   * integrator on 5 September 2026 when the mock body was re-recorded off the
   * real service.** The guess had been that the service scores a page the
   * reader marked as A, the way it scores one a library marked. It does not:
   * `work.tier` leaves a model finding out of the marks sum on purpose, so a
   * page whose every marked row came from the reader scores C, the topic-only
   * rung. Built to the guess, the card could never appear on a real page, and
   * a parent would have been told a page carrying eight marked questions had a
   * topic on it and nothing else.
   *
   * `allModelMarked` requires at least one finding, so a genuinely topic-only
   * page, which has none, is unaffected and still reads as topic-only. */
  /* The corpus is asked first, and the order matters. `noLibraryMarks` is true
     of a corpus page as well as a reader-marked one, so asking the reader
     first would put "not yet verified" over a page whose every mark we have
     agreed with ourselves about twice. The narrower claim is the truer one. */
  if (unlibraryTier(held) && allCorpusMarked(report)) return "corpus-marked";
  if (unlibraryTier(held) && noLibraryMarks(report)) return "model-marked";
  if (held) return held;

  if (report.image?.verdict === "poor") return "too-soft";
  const topics = report.topics ?? [];
  const findings = topics.flatMap((t) => t.findings ?? []);
  const resolved = topics.some((t) => t.status !== "none");
  if (!resolved) return "no-topic";
  if (findings.some((f) => f.status === "identified" || f.status === "ambiguous")) {
    return "explained";
  }
  if (findings.some((f) => f.status === "correct" || f.status === "unexplained")) {
    return "no-cause";
  }
  return "topic-only";
}

/** Which of the outcomes this page came to.
 *
 *  An error state is where the two halves separate. A read that failed on the
 *  photograph is `too-soft`, and everything else that ended a job is
 *  `not-read`: no credits left, today's free unreadable pages spent, a full
 *  queue, a restart, a read that broke on our side. Calling any of those
 *  "photo too soft" would send a parent to retake a photograph that was never
 *  the problem. */
export function outcomeOf(page: Judgeable): Outcome {
  if (page.state === "error") {
    return page.error?.kind === "quality" || page.quality?.verdict === "poor"
      ? "too-soft"
      : "not-read";
  }
  if (page.state === "done") return fromReport(page.report);
  return "reading";
}

/** Whether this outcome costs a credit, as a rule about outcomes rather than
 *  about one page. `creditCost` is what a screen asks about a page it holds;
 *  this is what a pricing explainer asks about the four in the abstract. */
export function isFree(outcome: Outcome): boolean {
  return outcome !== "explained" && outcome !== "no-cause";
}

/** Whether this page was marked from the corpus rather than by a library or by
 *  the reader on the spot.
 *
 *  Beside `isModelMarked` and for the same reason: one answer to the question
 *  in the product, rather than one per screen that asks it. It says nothing
 *  about money. Whether a corpus mark is billed is an admin setting on the
 *  service, `corpus_marks_bill`, off by default, and what it settled for this
 *  page arrives on the wire as `charged`. */
export function isCorpusMarked(page: Judgeable): boolean {
  return outcomeOf(page) === "corpus-marked";
}

/** Whether this page was marked by the reader with no library behind it.
 *
 *  For a screen that wants to label the rows as well as the card. Kept beside
 *  the outcome rather than re-derived in a screen, so there is one answer to
 *  the question in the product. */
export function isModelMarked(page: Judgeable): boolean {
  return outcomeOf(page) === "model-marked";
}

/** Whether the service actually stated what this page cost THIS learner.
 *
 *  A claim about a parent's money is not a thing to infer, and the two fields
 *  answer different questions. `charged` is about this request and settles it
 *  outright. `billable` is about the report, which is shared: `false` is a
 *  statement about everybody, because a page nobody may be charged for cost
 *  each of its owners nothing, and `true` says only that somebody paid.
 *
 *  So a marked page with no `charged` on it is a page whose cost to the parent
 *  looking at it is unstated, and a screen says nothing rather than printing
 *  a credit it cannot show was taken. That was the whole defect of the V1
 *  audit, and this function used to pass the state it was found in. The codex
 *  pass over the merged app diff on 5 September 2026 named it. */
export function costStated(page: Judgeable): boolean {
  if (page.state !== "done" && page.state !== "error") return false;
  /* Only a done body carries `charged`. A job that ended in error refunds its
     reservation on every path that ends one, so the service answers
     `billable: false` there and the line below settles it. */
  if (page.state === "done" && typeof page.charged === "boolean") return true;
  return page.billable === false;
}

/** What this page cost, in credits.
 *
 *  Read off the wire and never off the tier. That is not a style preference:
 *  the same rule written twice is the defect this codebase deleted once, and
 *  the service is the half that applies it.
 *
 *  `charged` is the answer where the service sent one, because it is the only
 *  field that is about this request rather than about the report. `billable`
 *  is the fallback and it is a weaker one: the same marked report is answered
 *  to every learner who sends the same bytes, and only the first of them paid
 *  for it, so a cache hit is billable and cost this learner nothing.
 *
 *  The two silences are answered the way the service answers them. A done page
 *  with neither field counts as charged, because `api.py` counts it that way
 *  when it totals a learner's month. A job that ended in error counts as free,
 *  because every path that ends one refunds its reservation. Ask `costStated`
 *  before printing either as a fact. */
export function creditCost(page: Judgeable): 0 | 1 {
  if (page.state !== "done") return 0;
  if (typeof page.charged === "boolean") return page.charged ? 1 : 0;
  return page.billable === false ? 0 : 1;
}

/** The one sentence a card shows for this page. */
export function headline(page: Judgeable): string {
  return OUTCOME_HEADLINE[outcomeOf(page)];
}

/** The mark total, labelled as what it is.
 *
 *  It ships, and it is an estimate from the evidence on the page. It is never
 *  the teacher's mark and never a grade of the child, and the label is not
 *  optional: it is the difference between a reading and a verdict. Null where
 *  the service stated no total, which is every page it did not mark and every
 *  paper with a page still missing. */
export function marksLine(report: Report | null | undefined): string | null {
  const marks = report?.marks;
  if (!marks || typeof marks.likely !== "number" ||
      typeof marks.available !== "number") return null;
  return `${marks.likely} of ${marks.available} marks, our estimate from `
    + "what is on the page";
}
